import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Rating,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { CreditApplication } from "../data/models/applications/CreditApplication";

function calculateCreditScore(credit: CreditApplication) {
  let points = 0;

  // ===== Regla 1: Puntualidad =====
  const onTimePayments = credit.installments?.every(
    (i) =>
      new Date(i.createdAt).getTime() <= new Date(credit.paymentDate).getTime()
  );
  if (onTimePayments) points += 2;

  // ===== Regla 2: Plazo =====
  if (credit.termMonths <= 6) points += 3;
  else if (credit.termMonths <= 12) points += 2;
  else points += 1;

  // ===== Regla 3: Finalización =====
  const approval = new Date(credit.approvalDate);
  approval.setMonth(approval.getMonth() + credit.termMonths);

  const now = new Date();
  if (now < approval) points += 1;
  else if (now > approval) points -= 1;

  // ===== Normalizar a 1–5 =====
  return Math.max(1, Math.min(5, points));
}

export function FinalizeCreditDialog({
  open,
  onClose,
  credit,
  finalizeApplication,
}: {
  open: boolean;
  onClose: () => void;
  credit: CreditApplication;
  finalizeApplication?: (
    id: string,
    qualification: number
  ) => Promise<boolean> | undefined;
}) {
  const [loading, setLoading] = useState(false);
  const autoScore = calculateCreditScore(credit);

  const [manualScore, setManualScore] = useState<number | null>(autoScore);

  const handleFinalize = async () => {
    try {
      setLoading(true);
      const ok = await finalizeApplication?.(
        credit.id!,
        manualScore ?? autoScore
      );
      if (ok) {
        onClose();
      }
    } catch (err) {
      console.error("Error finalizando crédito", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Finalizar Crédito</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Se calculará la calificación del usuario en base a su puntualidad de
          pago, plazo y fecha real de finalización.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
          <Rating
            name="credit-score"
            value={manualScore}
            precision={0.5} // ⭐ permite pasos de 0.5
            max={5}
            onChange={(_, newValue) => {
              setManualScore(newValue);
            }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            {manualScore}/5 ⭐
          </Typography>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Valor calculado automáticamente: {autoScore}/5
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleFinalize}
          variant="contained"
          color="success"
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {loading ? "Finalizando..." : "Confirmar Finalización"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
